import { useTranslation } from 'react-i18next';
import { PlanLayout } from "@/components/plans/PlanLayout";
import { CheckCircle, Cpu } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { motion } from "framer-motion";

const SentanSetsubiPage = () => {
  const { t } = useTranslation();

  const benefits = [
    { title: t('plans_detail.sentan_setsubi.benefit_0_title'), desc: t('plans_detail.sentan_setsubi.benefit_0_desc') },
    { title: t('plans_detail.sentan_setsubi.benefit_1_title'), desc: t('plans_detail.sentan_setsubi.benefit_1_desc') },
    { title: t('plans_detail.sentan_setsubi.benefit_2_title'), desc: t('plans_detail.sentan_setsubi.benefit_2_desc') },
  ];

  const equipment = [
    { title: t('plans_detail.sentan_setsubi.equip_0_title'), desc: t('plans_detail.sentan_setsubi.equip_0_desc') },
    { title: t('plans_detail.sentan_setsubi.equip_1_title'), desc: t('plans_detail.sentan_setsubi.equip_1_desc') },
    { title: t('plans_detail.sentan_setsubi.equip_2_title'), desc: t('plans_detail.sentan_setsubi.equip_2_desc') },
    { title: t('plans_detail.sentan_setsubi.equip_3_title'), desc: t('plans_detail.sentan_setsubi.equip_3_desc') },
  ];

  const steps = [
    t('plans_detail.sentan_setsubi.step_0'),
    t('plans_detail.sentan_setsubi.step_1'),
    t('plans_detail.sentan_setsubi.step_2'),
    t('plans_detail.sentan_setsubi.step_3'),
    t('plans_detail.sentan_setsubi.step_4'),
  ];

  return (
    <PlanLayout
      title={t('plans_detail.sentan_setsubi.title')}
      imageUrl="https://images.unsplash.com/photo-1518770660439-4636190af475"
    >
      <div className="space-y-8 mb-16">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 mt-8 text-white">{t('plans_detail.common.overview')}</h2>
          <p className="mb-8 text-white/70 leading-relaxed">
            {t('plans_detail.sentan_setsubi.overview_p1')}
          </p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.sentan_setsubi.benefits_title')}</h2>
          <motion.div
            className="space-y-4 mb-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {benefits.map(({ title, desc }) => (
              <motion.div
                key={title}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                whileHover={{ scale: 1.02 }}
                className="border border-white/10 bg-white/5 p-6 rounded-xl flex items-start gap-4"
              >
                <CheckCircle className="text-[#c9a84c] mt-1 h-6 w-6 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-2 text-white">{title}</h3>
                  <p className="text-white/65 leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.sentan_setsubi.equipment_title')}</h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {equipment.map(({ title, desc }) => (
              <motion.div
                key={title}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                whileHover={{ scale: 1.02 }}
                className="border border-white/10 bg-white/5 p-6 rounded-xl"
              >
                <Cpu className="text-[#c9a84c] h-6 w-6 mb-3" />
                <h3 className="font-bold text-lg mb-2 text-white">{title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.sentan_setsubi.steps_title')}</h2>
          <ol className="list-decimal pl-6 mb-8 space-y-2 text-white/70">
            {steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="border border-[#c9a84c]/30 bg-[#c9a84c]/10 p-6 rounded-xl mb-8">
            <h3 className="font-bold text-lg mb-3 text-[#c9a84c]">{t('plans_detail.common.useful_links')}</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.chusho.meti.go.jp/keiei/seisansei/index.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#c9a84c] hover:underline text-sm"
                >
                  {t('plans_detail.sentan_setsubi.link_0_text')}
                </a>
              </li>
              <li>
                <a
                  href="https://www.chusho.meti.go.jp/keiei/seisansei/2022/220401seisansei.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#c9a84c] hover:underline text-sm"
                >
                  {t('plans_detail.sentan_setsubi.link_1_text')}
                </a>
              </li>
            </ul>
          </div>
        </motion.section>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="border-t border-white/10 pt-10 mt-10"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-white">{t('plans_detail.common.contact_title')}</h2>
        <div className="border border-white/10 bg-white/5 p-6 rounded-xl shadow-sm">
          <p className="mb-6 text-center text-white/65 leading-relaxed">
            {t('plans_detail.sentan_setsubi.contact_desc')}
          </p>
          <ContactForm
            buttonText={t('plans_detail.sentan_setsubi.contact_button')}
            className="border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10"
          />
        </div>
      </motion.div>
    </PlanLayout>
  );
};

export default SentanSetsubiPage;
